import React, { useState, useRef, useEffect } from 'react';
import { Send, RefreshCw, ChevronDown, Loader, Eye, BarChart2, Globe, Layout, Paperclip } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import SectionTitle from './text_components/SectionTitle';

const topics = ['ERISA', 'FINRA', 'DOL', '5500'];

const suggestions = [
  { icon: Eye, text: "What is a highly compensated employee?" },
  { icon: BarChart2, text: "Explain the plan sponsor's fiduciary duties" },
  { icon: Layout, text: "How can I ensure my plan is ERISA compliant?" },
  { icon: Globe, text: "What are the 401(k) contribution limits?" },
];

const AIAssistantFullPage = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [selectedTopic, setSelectedTopic] = useState(topics[0]);
  const [isTyping, setIsTyping] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const messagesEndRef = useRef(null);
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSendMessage = (content = inputMessage) => {
    if (content.trim() !== '') {
      setMessages([...messages, { type: 'user', content }]);
      setInputMessage('');
      setIsTyping(true);
      // Simulate AI response
      setTimeout(() => {
        setIsTyping(false);
        setMessages(prev => [...prev, { type: 'ai', content: `This is a simulated AI response for "${content}" related to ${selectedTopic}${uploadedFile ? ` and the uploaded document "${uploadedFile.name}"` : ''}.` }]);
      }, 2000);
    }
  };

  const handleReset = () => {
    setMessages([]);
    setInputMessage('');
    setUploadedFile(null);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFile(file);
      setMessages(prev => [...prev, { type: 'system', content: `File "${file.name}" has been uploaded.` }]);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        when: "beforeChildren",
        staggerChildren: 0.1,
        duration: 0.3,
        ease: "easeInOut"
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const suggestionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.2,
        duration: 0.8,
        ease: "easeOut"
      }
    })
  };

  return (
    <motion.div 
      className="flex flex-col h-screen"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Chat area */}
      <motion.div className="flex-grow overflow-auto p-14" variants={itemVariants}>
        <div className="max-w-3xl mx-auto space-y-4">
          {uploadedFile && (
            <motion.div 
              className="bg-blue-100 text-gray-800 p-3 rounded-lg mb-4"
              variants={itemVariants}
            >
              <p className="text-sm">Uploaded file: {uploadedFile.name}</p>
            </motion.div>
          )}
          {messages.length === 0 ? (
            <motion.div className="text-center" variants={itemVariants}>
              <SectionTitle>PlanSync AI AI Assistant</SectionTitle>
              <motion.div 
                className="grid grid-cols-1 md:grid-cols-2 gap-4"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {suggestions.map((suggestion, index) => (
                  <motion.button
                    key={index}
                    custom={index}
                    variants={suggestionVariants}
                    className="bg-gradient-to-bl from-sky-400 to-blue-800 text-left text-white p-4 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 ease-in-out flex items-start space-x-3 border border-blue-200 shadow-xl"
                    onClick={() => handleSendMessage(suggestion.text)}
                  >
                    <suggestion.icon className="w-5 h-5 text-white flex-shrink-0" />
                    <span className="text-sm">{suggestion.text}</span>
                  </motion.button>
                ))}
              </motion.div>
            </motion.div>
          ) : (
            <AnimatePresence>
              {messages.map((message, index) => (
                <motion.div 
                  key={index} 
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                >
                  <div 
                    className={`max-w-xs md:max-w-md lg:max-w-lg xl:max-w-xl rounded-2xl px-4 py-2 shadow-sm break-words border border-blue-200 shadow-lg ${
                      message.type === 'user' 
                        ? 'bg-gradient-to-bl from-sky-400 to-blue-800 text-white' 
                        : message.type === 'system'
                        ? 'bg-gray-200 text-gray-800'
                        : 'bg-white text-gray-800'
                    }`}
                  >
                    <p className="text-sm">{message.content}</p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          )}
          {isTyping && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="flex items-center text-blue-600 bg-white rounded-full px-4 py-2 shadow-sm w-max border border-blue-200 shadow-lg"
            >
              <Loader className="w-4 h-4 mr-2 animate-spin" />
              <span className="text-sm font-medium">Finding your answer...</span>
            </motion.div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </motion.div>

      {/* Input area */}
      <motion.div 
        className="border-t border-gray-200 p-4 bg-white"
        variants={itemVariants}
      >
        <div className="max-w-3xl mx-auto">
          <motion.div 
            className="flex items-center bg-white border border-gray-300 rounded-full shadow-sm hover:border-blue-500 focus-within:border-blue-500"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current.click()}
              className="p-2 text-gray-500 hover:text-gray-700"
            >
              <Paperclip size={20} className="ml-3 hover:text-blue-500 focus-within:text-blue-500" />
            </button>
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder="Send a message..."
              className="flex-grow px-4 py-2 bg-transparent outline-none"
            />
            <button 
              onClick={() => handleSendMessage()}
              className="p-2 text-gray-500 hover:text-gray-700"
            >
              <Send size={20} className="transform rotate-45 mr-3 hover:text-blue-500 focus-within:text-blue-500" />
            </button>
          </motion.div>
          <motion.div 
            className="flex justify-between items-center mt-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8, ease: "easeOut" }}
          >
            <p className="text-xs text-center text-gray-500 w-full">
              PlanSync AI's AI Assistant can make mistakes. Be sure to always check important information. This is not legal or financial advice.
            </p>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AIAssistantFullPage;